import React, { useState, useEffect } from 'react';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';
import SVOPanel from './SVOPanel';

const WordChips = ({ sentence, color }) => {
  const words = sentence.split(' ');
  return (
    <div className="flex flex-wrap gap-1">
      {words.map((word, i) => (
        <span
          key={i}
          className={`px-2 py-0.5 rounded text-sm font-medium ${color}`}
        >
          {word}
        </span>
      ))}
    </div>
  );
};

const ExamplesPanel = ({ examples }) => {
  if (!examples || examples.length === 0) return null;
  return (
    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
      <div className="text-xs font-semibold text-blue-600 mb-2 uppercase tracking-wide">Příklady se stejnou strukturou</div>
      <div className="flex flex-col gap-3">
        {examples.map((ex, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-6">CZ</span>
              <WordChips sentence={ex.czech} color="bg-blue-100 text-blue-800" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-6">EN</span>
              <WordChips sentence={ex.english} color="bg-green-100 text-green-800" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GrammarFillInExercise = ({
  exercises,
  currentExerciseIndex,
  wordOptions,
  userAnswers,
  revealedAnswers = new Set(),
  onAnswerDrop,
  onReveal,
  onNext,
  onPrevious,
  onSubmit,
  topic,
  supportLevel,
}) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [grammarNoteOpen, setGrammarNoteOpen] = useState(false);
  const [openExamples, setOpenExamples] = useState(new Set());

  const grammarNotes = [...new Set(exercises.map(ex => ex.grammar_note).filter(Boolean))];

  // Auto-select first empty exercise on load
  useEffect(() => {
    if (exercises.length > 0) {
      const firstEmpty = exercises.find(ex => !userAnswers[ex.id]);
      if (firstEmpty) {
        setSelectedExercise(firstEmpty.id);
      }
    }
  }, [exercises, userAnswers]);

  const handleBlankClick = (exerciseId) => {
    setSelectedExercise(exerciseId);
  };

  const handleWordClick = (word) => {
    if (selectedExercise) {
      onAnswerDrop(selectedExercise, word);
      // Auto-select next empty exercise
      const currentIndex = exercises.findIndex(ex => ex.id === selectedExercise);
      const nextEmpty = exercises.slice(currentIndex + 1).find(ex => !userAnswers[ex.id]);
      setSelectedExercise(nextEmpty ? nextEmpty.id : null);
    }
  };

  const toggleExamples = (exerciseId) => {
    setOpenExamples(prev => {
      const next = new Set(prev);
      if (next.has(exerciseId)) {
        next.delete(exerciseId);
      } else {
        next.add(exerciseId);
      }
      return next;
    });
  };

  const allAnswered = exercises.every(ex => userAnswers[ex.id]);

  return (
    <ExerciseCard>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold text-gray-800">
          Doplňovací cvičení
        </h2>
        <div className="text-sm font-medium text-gray-500">
          {Object.keys(userAnswers).length} / {exercises.length}
        </div>
      </div>

      {/* Grammar note chip */}
      {grammarNotes.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setGrammarNoteOpen(!grammarNoteOpen)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all cursor-pointer border border-blue-200"
          >
            <span>ℹ️</span>
            <span>Pravidlo</span>
            <span className={`transition-transform ${grammarNoteOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {grammarNoteOpen && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
              {grammarNotes.map((note, i) => (
                <p key={i} className={i > 0 ? 'mt-1' : ''}>{note}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Exercises list */}
      <div className="flex flex-col gap-4 mb-6">
        {exercises.map((exercise, index) => {
          const userAnswer = userAnswers[exercise.id];
          const isRevealed = revealedAnswers.has(exercise.id);
          const isSelected = selectedExercise === exercise.id;
          const hasExamples = exercise.examples && exercise.examples.length > 0;
          const isOpen = openExamples.has(exercise.id);

          // Split sentence by "__"
          const parts = exercise.sentence.split(/_{2,}/);

          return (
            <div
              key={exercise.id}
              className={`p-4 rounded-lg flex flex-col gap-2 transition-all ${
                isSelected
                  ? 'bg-purple-50 ring-2 ring-purple-200'
                  : userAnswer
                    ? 'bg-green-50'
                    : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-500 font-semibold min-w-[30px]">
                  {index + 1}.
                </span>

                <TranslatableText text={parts[0]} textColor="#374151" topic={topic} />

                {/* Answer blank - clickable to select */}
                <span
                  onClick={() => !isRevealed && handleBlankClick(exercise.id)}
                  className={`inline-block min-w-[120px] px-4 py-1.5 rounded text-center transition-all font-medium ${
                    isRevealed
                      ? 'bg-blue-50 text-blue-700 border-2 border-blue-300 cursor-default'
                      : userAnswer
                        ? 'bg-green-100 text-green-700 border-2 border-green-400 cursor-pointer'
                        : isSelected
                          ? 'bg-amber-50 text-gray-600 border-2 border-amber-400 cursor-pointer'
                          : 'bg-white text-gray-400 border-2 border-dashed border-purple-400 cursor-pointer'
                  }`}
                >
                  {userAnswer || '__'}
                </span>
                {parts[1] && <TranslatableText text={parts[1]} textColor="#374151" topic={topic} />}

                {/* Nevím button — shown when not yet answered or revealed */}
                {supportLevel !== 'independent' && !userAnswer && !isRevealed && (
                  <button
                    onClick={() => onReveal(exercise.id)}
                    className="ml-2 px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-600 rounded transition-all"
                  >
                    Nevím
                  </button>
                )}
                {isRevealed && (
                  <span className="ml-2 text-sm text-blue-600 font-medium">
                    Odpověď: {exercise.correct_answer}
                  </span>
                )}

                {hasExamples && (
                  <button
                    onClick={() => toggleExamples(exercise.id)}
                    className="ml-auto text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1 shrink-0"
                  >
                    <span>{isOpen ? '▼' : '▶'}</span>
                    <span>Příklady</span>
                  </button>
                )}
              </div>

              {isOpen && <ExamplesPanel examples={exercise.examples} />}
              <SVOPanel exercise={exercise} />
            </div>
          );
        })}
      </div>

      {/* Word options - clickable */}
      <div className="mb-6 p-4 bg-gray-50 rounded-xl">
        <div className="text-sm text-gray-500 mb-3 font-medium">
          {selectedExercise ? 'Klikněte na správné slovo:' : 'Nejprve klikněte na mezeru, kterou chcete vyplnit'}
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          {wordOptions.map((word, index) => (
            <button
              key={index}
              onClick={() => handleWordClick(word)}
              disabled={!selectedExercise}
              className={`px-6 py-2.5 rounded-lg text-lg font-medium transition-all ${
                selectedExercise
                  ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={onSubmit}
          disabled={!allAnswered}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            allAnswered
              ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Odevzdat odpovědi
        </button>
      </div>

      {!allAnswered && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Doplňte všechna cvičení před odevzdáním
        </div>
      )}
    </ExerciseCard>
  );
};

export default GrammarFillInExercise;
